import React from "react";
import {
  Type,
  FileText,
  List,
  CheckSquare,
  Circle,
  Sliders,
  Palette,
  Link,
  BarChart3,
  Hash,
  Check,
} from "lucide-react";
import { QUESTION_TYPES } from "../models/types";

const QuestionTypeSelector = ({ onSelectType, selectedType = null }) => {
  const questionTypes = [
    {
      type: QUESTION_TYPES.TEXT,
      icon: Type,
      title: "Short Answer",
      description: "Single line text input",
      color: "text-blue-400",
      bgColor: "bg-blue-500/10",
      borderColor: "border-blue-500/30",
      selectedBgColor: "bg-blue-500/20",
      selectedBorderColor: "border-blue-400",
    },
    {
      type: QUESTION_TYPES.TEXTAREA,
      icon: FileText,
      title: "Paragraph",
      description: "Multi-line text input",
      color: "text-green-400",
      bgColor: "bg-green-500/10",
      borderColor: "border-green-500/30",
      selectedBgColor: "bg-green-500/20",
      selectedBorderColor: "border-green-400",
    },
    {
      type: QUESTION_TYPES.SELECT,
      icon: List,
      title: "Dropdown",
      description: "Single selection dropdown",
      color: "text-purple-400",
      bgColor: "bg-purple-500/10",
      borderColor: "border-purple-500/30",
      selectedBgColor: "bg-purple-500/20",
      selectedBorderColor: "border-purple-400",
    },
    {
      type: QUESTION_TYPES.RADIO,
      icon: Circle,
      title: "Multiple Choice",
      description: "Single selection radio buttons",
      color: "text-orange-400",
      bgColor: "bg-orange-500/10",
      borderColor: "border-orange-500/30",
      selectedBgColor: "bg-orange-500/20",
      selectedBorderColor: "border-orange-400",
    },
    {
      type: QUESTION_TYPES.CHECKBOX,
      icon: CheckSquare,
      title: "Checkboxes",
      description: "Multiple selections allowed",
      color: "text-red-400",
      bgColor: "bg-red-500/10",
      borderColor: "border-red-500/30",
      selectedBgColor: "bg-red-500/20",
      selectedBorderColor: "border-red-400",
    },
    {
      type: QUESTION_TYPES.MULTISELECT,
      icon: List,
      title: "Multi-Select",
      description: "Multiple selections dropdown",
      color: "text-pink-400",
      bgColor: "bg-pink-500/10",
      borderColor: "border-pink-500/30",
      selectedBgColor: "bg-pink-500/20",
      selectedBorderColor: "border-pink-400",
    },
    {
      type: QUESTION_TYPES.SCALE_1_10,
      icon: BarChart3,
      title: "Linear Scale",
      description: "1-10 rating scale",
      color: "text-indigo-400",
      bgColor: "bg-indigo-500/10",
      borderColor: "border-indigo-500/30",
      selectedBgColor: "bg-indigo-500/20",
      selectedBorderColor: "border-indigo-400",
    },
    {
      type: QUESTION_TYPES.SLIDER,
      icon: Sliders,
      title: "Slider",
      description: "Range input with slider",
      color: "text-teal-400",
      bgColor: "bg-teal-500/10",
      borderColor: "border-teal-500/30",
      selectedBgColor: "bg-teal-500/20",
      selectedBorderColor: "border-teal-400",
    },
    {
      type: QUESTION_TYPES.COLOR,
      icon: Palette,
      title: "Color Picker",
      description: "Visual color selection",
      color: "text-pink-400",
      bgColor: "bg-pink-500/10",
      borderColor: "border-pink-500/30",
      selectedBgColor: "bg-pink-500/20",
      selectedBorderColor: "border-pink-400",
    },
    {
      type: QUESTION_TYPES.URL,
      icon: Link,
      title: "Website URL",
      description: "URL input with validation",
      color: "text-cyan-400",
      bgColor: "bg-cyan-500/10",
      borderColor: "border-cyan-500/30",
      selectedBgColor: "bg-cyan-500/20",
      selectedBorderColor: "border-cyan-400",
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {questionTypes.map((questionType) => {
        const Icon = questionType.icon;
        const isSelected = selectedType === questionType.type;

        return (
          <button
            key={questionType.type}
            onClick={() => onSelectType(questionType.type)}
            className={`
              relative p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-lg hover:scale-105
              ${
                isSelected
                  ? `${questionType.selectedBgColor} ${questionType.selectedBorderColor} border-2 shadow-lg ring-2 ring-primary-green/50`
                  : "bg-neutral-800/60 border-neutral-700 hover:border-neutral-600 hover:bg-neutral-800/80"
              }
            `}
          >
            {/* Selection indicator */}
            {isSelected && (
              <div className="absolute -top-1 -right-1 bg-primary-green rounded-full p-1">
                <Check size={12} className="text-white" />
              </div>
            )}

            <div className="flex flex-col items-center text-center space-y-2">
              <div
                className={`
                p-3 rounded-full transition-colors
                ${isSelected ? questionType.bgColor : "bg-neutral-700/50"}
              `}
              >
                <Icon
                  size={24}
                  className={
                    isSelected
                      ? questionType.color
                      : questionType.color.replace("400", "500")
                  }
                />
              </div>
              <div>
                <h3
                  className={`font-medium text-sm ${
                    isSelected ? "text-white" : "text-neutral-300"
                  }`}
                >
                  {questionType.title}
                </h3>
                <p
                  className={`text-xs ${
                    isSelected ? "text-neutral-400" : "text-neutral-500"
                  }`}
                >
                  {questionType.description}
                </p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default QuestionTypeSelector;
